import { money } from "@/lib/roof";
import { useI18n } from "@/i18n/I18nContext";

export default function EstimateBreakdown({ calc, settings, sections, footprintSqft, roofType, pitchLabel, masquerListe = false }) {
  const { t } = useI18n();
  const multi = sections && sections.length > 1;
  const rtLabel = (v) => (v ? t(`roofTypes.${String(v).toLowerCase()}`) : "—");
  return (
    <div className="space-y-4">
      <div className="bg-surface border border-line rounded-xl p-6 shadow-carte">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-ink-faint mb-4">{t("breakdown.measurement")}</h3>

        {/* La liste des bâtiments est masquée quand le récapitulatif « Total
            mesuré » la montre déjà juste au-dessus : deux fois la même liste
            fait douter qu'il s'agisse bien de la même chose. */}
        {!masquerListe && sections && sections.length > 0 && (
          <div className="space-y-2 text-sm mb-4">
            {sections.map((s, i) => (
              <div key={i} className="flex justify-between" data-testid={`section-summary-${i}`}>
                <span className="text-ink-soft truncate pr-2">
                  {s.label} <span className="text-ink-faint">· {s.pitchLabel}</span>
                </span>
                <span className="font-medium whitespace-nowrap">{Math.round(s.roofArea).toLocaleString()} {t("units.sqft")}</span>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-y-3 text-sm border-t border-line pt-4">
          {!multi && roofType !== undefined && (
            <>
              <span className="text-ink-soft">{t("breakdown.roofType")}</span>
              <span className="text-right font-medium" data-testid="roof-type-value">{rtLabel(roofType)}</span>
              <span className="text-ink-soft">{t("breakdown.pitch")}</span>
              <span className="text-right font-medium">{pitchLabel}</span>
            </>
          )}
          <span className="text-ink-soft">{t("breakdown.footprint")}</span>
          <span className="text-right font-medium" data-testid="footprint-value">{Math.round(footprintSqft).toLocaleString()} {t("units.sqft")}</span>
          <span className="text-ink-soft">{t("breakdown.roofSurface")}</span>
          <span className="text-right font-medium" data-testid="roof-area-value">{Math.round(calc.roofArea).toLocaleString()} {t("units.sqft")}</span>
          <span className="text-ink-soft">{t("breakdown.squares")}</span>
          <span className="text-right font-medium">{calc.totalSquares.toFixed(1)} {t("units.sq")}</span>
          <span className="text-ink-soft">{t("breakdown.inclWaste", { n: settings.waste_percent })}</span>
          <span className="text-right font-medium">{calc.adjustedSquares.toFixed(1)} {t("units.sq")}</span>
        </div>
      </div>

      <div className="bg-surface border border-line rounded-xl p-6 shadow-carte">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-ink-faint mb-4">{t("breakdown.costBreakdown")}</h3>
        <div className="space-y-2 text-sm">
          {calc.lineItems.map((li, i) => (
            <div key={i} className="flex justify-between">
              <span className="text-ink-soft">{li.name}</span>
              <span className="font-medium">{money(li.amount)}</span>
            </div>
          ))}
          <div className="flex justify-between pt-2 border-t border-line">
            <span className="text-ink-soft">{t("breakdown.materials")}</span>
            <span className="font-medium" data-testid="materials-cost">{money(calc.materialsCost)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-soft">{t("breakdown.labor")}</span>
            <span className="font-medium" data-testid="labor-cost">{money(calc.laborCost)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-soft">{t("breakdown.subtotal")}</span>
            <span className="font-medium">{money(calc.subtotal)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-soft">{t("breakdown.tax")} ({settings.tax_percent}%)</span>
            <span className="font-medium">{money(calc.tax)}</span>
          </div>
          <div className="flex justify-between items-center pt-3 mt-2 border-t border-line">
            <span className="font-heading font-bold uppercase tracking-wide">{t("breakdown.total")}</span>
            <span className="font-heading text-2xl font-extrabold text-brand-ink" data-testid="total-cost">{money(calc.total)}</span>
          </div>
        </div>

        {/* Calculateur de profit — visible au couvreur seulement, jamais sur le PDF client. */}
        {calc.profit != null && calc.costTotal > 0 && (
          <div data-testid="profit-panel" className="mt-4 border border-emerald-500/30 bg-emerald-500/5 rounded-md p-4 space-y-1.5 text-sm">
            <div className="flex justify-between text-xs font-bold uppercase tracking-[0.15em] text-emerald-400">
              <span>{t("breakdown.profitTitle")}</span>
              <span className="font-normal normal-case tracking-normal text-ink-faint">{t("breakdown.internalOnly")}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-ink-soft">{t("breakdown.costTotal")}</span>
              <span className="font-medium" data-testid="cost-total">{money(calc.costTotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-ink-soft">{t("breakdown.profit")}</span>
              <span className="font-bold text-emerald-400" data-testid="profit-amount">{money(calc.profit)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-ink-soft">{t("breakdown.margin")}</span>
              <span className="font-medium text-emerald-400">{calc.subtotal > 0 ? ((calc.profit / calc.subtotal) * 100).toFixed(1) : "0"} %</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
